// CostOfWork.tsx
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { useState } from 'react';
import { Save } from '@mui/icons-material';
import EstimatedCostOfWork from '../CommonSubcodeComponents/EstimatedCostOfWork';
import { RootState } from 'src/redux-store/appStore';
import { useDispatch, useSelector } from 'react-redux';
import { updateBuilding } from 'src/redux-store/slices/constructionFormSlice';
import toast from 'react-hot-toast';
import { httpRequest } from 'src/services/httpRequest';
import { useParams } from 'react-router-dom';

interface WorkItem {
  name?: string;
  Item: string;
  Size?: number;
  check: boolean;
  Cost: number;
  WallArea?: string | null;
  FenceHeight?: string | null;
  SignType?: string | null;
  SignArea?: string | null;
  PoolItem?: string | null;
  PoolArea?: string | null;
  WallType?: string | null;
  RoofReplacementType?: string | null;
}

interface EstimatedCosts {
  NewBuildingCost: number | null;
  RehabilitationCost: number | null;
  DemolitionCost: number | null;
  NewVolume: number | null;
  NewArea: number | null;
}

interface CostOfWorkProps {
  estimatedCostOfWork: EstimatedCosts;
  onBack: () => void;
  onNext: () => void;
  townshipID: number;
}

const CostOfWork: React.FC<CostOfWorkProps> = ({
  estimatedCostOfWork,
  onBack,
  onNext,
  townshipID
}) => {
  const dispatch = useDispatch();
  const [isError, setIsError] = useState(false);
  const {townshipId}= useParams();
  const [otherWorkItemsName, setOtherWorkItemsName] = useState([
    { Item: '', Size: 0, Cost: 0, check: true, Type: 'other' }
  ]);
  const workItems = useSelector(
    (state: RootState) => state.constructionFormSlice?.Building?.WorkItems
  );

  const handleWorkItemChange = (
    index: number,
    field: keyof WorkItem,
    value: string | boolean
  ) => {
    const updatedWorkItems = [...workItems];
    updatedWorkItems[index] = { ...updatedWorkItems[index], [field]: value };
    // Update only the nested property
    if (isNaN(Number(value))) {
      dispatch(
        updateBuilding({
          WorkItems: updatedWorkItems
        })
      );
    } else if (Number(value) >= 0) {
      dispatch(
        updateBuilding({
          WorkItems: updatedWorkItems
        })
      );
    }
  };

  async function getBuildingFees() {
    const payload = {
      UseGroup: 'R-3, R-5',
      IsResidential: false,
      CertificateType: 'Clearance',
      BuildingSize:
        estimatedCostOfWork?.NewVolume * estimatedCostOfWork?.NewArea,
      WorkItems: workItems,
      TownshipID:townshipId || townshipID
    };
    const response = await httpRequest(
      'POST',
      `/construction/building/get_building_fees`,
      payload
    );

    if (response?.status === 'success') {
      const data = response?.data || '';
      const dataDetails = {
        'Building Size': data['Building Size'],
        'Certification Cost': data['Certification Cost'],
        totalFee: data.totalFee,
        summary: data.summary
      };
      dispatch(
        updateBuilding({
          getAllFee: dataDetails
        })
      );
      // setLoading(false);
    } else {
      // setLoading(false);
      toast.error(response?.message);
    }
  }

  return (
    <Grid container gap={3}>
      {/* Left Grid - Estimation fields */}
      <Grid item md={3} sm={4}>
        {/* Estimated Cost of Work */}
        <EstimatedCostOfWork isError={isError} />
      </Grid>

      <Divider orientation="vertical" variant="middle" flexItem />

      {/* Right Grid - Work items */}
      <Grid item md={8} sm={6}>
        <Typography variant="h5">Construction Work items</Typography>
        <br />
        <Grid container spacing={3}>
          {workItems?.map((item, index) => (
            <Grid
              item
              xs={12}
              md={4}
              sm={6}
              key={`${item.Item}-${index}`}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={item.check}
                    onChange={(e) =>
                      handleWorkItemChange(index, 'check', e.target.checked)
                    }
                  />
                }
                label={item.Item}
              />

              {item.check && (
                <>
                  {item?.name === 'other' && (
                    <>
                      <TextField
                        type="number"
                        fullWidth
                        label="Cost"
                        variant="outlined"
                        value={item.Cost || 0}
                        onChange={(e) =>
                          handleWorkItemChange(index, 'Cost', e.target.value)
                        }
                      />
                    </>
                  )}

                  {item.Item === 'Retaining Wall' && (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>Select item</InputLabel>
                        <Select
                          label="Select item"
                          value={item?.WallType || ''}
                          onChange={(e) =>
                            handleWorkItemChange(
                              index,
                              'WallType',
                              e.target.value as string
                            )
                          }
                        >
                          <MenuItem value="RetainingWallAbove550SqFtClass3">
                            Retaining Wall Above 550 SqFt Class3
                          </MenuItem>
                          <MenuItem value="RetainingWallUnder550SqFtClass3">
                            Retaining Wall Under 550 SqFt Class3
                          </MenuItem>
                          <MenuItem value="RetainingWallOtherStructure">
                            Retaining Wall Other Structure
                          </MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        type="number"
                        fullWidth
                        label="Retaining Wall Area (sqft)"
                        variant="outlined"
                        value={item.WallArea || ''}
                        onChange={(e) =>
                          handleWorkItemChange(
                            index,
                            'WallArea',
                            e.target.value
                          )
                        }
                      />
                    </>
                  )}

                  {item.Item === 'Roofing' && (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>Select item</InputLabel>
                        <Select
                          label="Select item"
                          value={item?.RoofReplacementType || ''}
                          onChange={(e) =>
                            handleWorkItemChange(
                              index,
                              'RoofReplacementType',
                              e.target.value as string
                            )
                          }
                        >
                          <MenuItem value="RoofReplacementR">
                            Roof Replacement-R
                          </MenuItem>
                          <MenuItem value="RoofReplacementRWithPlywood">
                            Roof Replacement-R With Plywood
                          </MenuItem>
                          <MenuItem value="CommercialRoofReplacement">
                            Commercial Roof Replacement
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </>
                  )}

                  {item.Item === 'Addition' && (
                    <TextField
                      type="number"
                      fullWidth
                      label="Estimated Cost"
                      variant="outlined"
                      value={item.Cost || 0}
                      onChange={(e) =>
                        handleWorkItemChange(index, 'Cost', e.target.value)
                      }
                    />
                  )}

                  {item.Item === 'Fence' && (
                    <TextField
                      type="number"
                      fullWidth
                      label="Fence Height (exceeds 6ft)"
                      variant="outlined"
                      value={item.FenceHeight || ''}
                      onChange={(e) =>
                        handleWorkItemChange(
                          index,
                          'FenceHeight',
                          e.target.value
                        )
                      }
                    />
                  )}

                  {item.Item === 'Sign' && (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>Select item</InputLabel>
                        <Select
                          label="Select item"
                          value={item.SignType || ''}
                          onChange={(e) =>
                            handleWorkItemChange(
                              index,
                              'SignType',
                              e.target.value as string
                            )
                          }
                        >
                          <MenuItem value="FreestandingSign">
                            Free standing Sign
                          </MenuItem>
                          <MenuItem value="WallSign">Wall Sign</MenuItem>
                          <MenuItem value="MinimumSignFee">
                            Minimum Sign Fee
                          </MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        type="number"
                        fullWidth
                        label="Sign Area (sqft)"
                        variant="outlined"
                        value={item.SignArea || ''}
                        onChange={(e) =>
                          handleWorkItemChange(
                            index,
                            'SignArea',
                            e.target.value
                          )
                        }
                      />
                    </>
                  )}

                  {item.Item === 'Pool' && (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>Select item</InputLabel>
                        <Select
                          label="Select item"
                          value={item.PoolItem || ''}
                          onChange={(e) =>
                            handleWorkItemChange(
                              index,
                              'PoolItem',
                              e.target.value as string
                            )
                          }
                        >
                          <MenuItem value="SwimmingPoolAboveGround">
                            Swimming Pool Above-Ground
                          </MenuItem>
                          <MenuItem value="SwimmingPoolInGround">
                            Swimming Pool In-Ground
                          </MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        type="number"
                        fullWidth
                        label="Pool Area (sqft)"
                        variant="outlined"
                        value={item.PoolArea || ''}
                        onChange={(e) =>
                          handleWorkItemChange(
                            index,
                            'PoolArea',
                            e.target.value
                          )
                        }
                      />
                    </>
                  )}
                </>
              )}
            </Grid>
          ))}
        </Grid>
        <br />
        <br />

        {/* Add More Items */}
        <Grid
          item
          xs={12}
          key={`${'item.item'} + index`}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <Typography variant="h5">Add More Items</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'column', md: 'row' },
              gap: 2
            }}
          >
            <TextField
              name="itemName"
              label="Other item name"
              fullWidth
              value={otherWorkItemsName[0].Item}
              onChange={(e) => {
                const updatedWorkItems = [...otherWorkItemsName];
                updatedWorkItems[0].Item = e.target.value;
                setOtherWorkItemsName(updatedWorkItems);
              }}
            />

            <TextField
              name="size"
              label="Fee"
              type="number"
              fullWidth
              value={otherWorkItemsName[0].Cost}
              onChange={(e) => {
                if (Number(e.target.value) >= 0) {
                  const updatedWorkItems = [...otherWorkItemsName];
                  updatedWorkItems[0].Cost = Number(e.target.value);
                  setOtherWorkItemsName(updatedWorkItems);
                }
              }}
            />

            <Button
              variant="contained"
              color={'primary'}
              startIcon={<Save />}
              onClick={() => {
                if (otherWorkItemsName[0].Item !== '') {
                  dispatch(
                    updateBuilding({
                      WorkItems: [...workItems, ...otherWorkItemsName]
                    })
                  );
                  setOtherWorkItemsName([
                    {
                      Item: '',
                      Size: 0,
                      Cost: 0,
                      check: true,
                      Type: 'other'
                    }
                  ]);
                }
              }}
              fullWidth
            >
              {'Add Items'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Form Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 3,
          width: '100%'
        }}
      >
        <Button onClick={onBack} variant="contained" color="inherit">
          Back
        </Button>
        <Button
          onClick={async () => {
            let isEstimatedCostNull = true;
            for (const key in estimatedCostOfWork) {
              if (
                estimatedCostOfWork[key] === null ||
                estimatedCostOfWork[key] === 0
              ) {
                isEstimatedCostNull = false;
              }
            }
            setIsError(!isEstimatedCostNull);
            if (isEstimatedCostNull) {
              await getBuildingFees();
              onNext();
            }
          }}
          variant="contained"
        >
          Save
        </Button>
      </Box>
    </Grid>
  );
};

export default CostOfWork;
